import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert } from 'antd'

import useTranslation from 'hooks/useTranslation'
import useRequestState from 'hooks/useRequestState'
import { login } from 'services/auth'

import Button from 'components/Button'
import Icon from 'components/Icon'
import Testimonials from 'components/Testimonials'
import { Form, ErrorAlert, Input, Store } from 'components/Form'
import { Heading, Text } from 'components/Typography'
import OTPForm from 'containers/OTPForm'
import session, { SessionStorageKey } from 'utils/sessionStorage'

import styles from './Login.module.scss'

type FormValues = {
  email: string
  password: string
}

const Login = () => {
  const [otpForm, setOtpForm] = useState(false)
  const [email, setEmail] = useState('')
  const [sessionTimedOut, setSessionTimedOut] = useState(false)
  const state = useRequestState()
  const { t, copy } = useTranslation()

  useEffect(() => {
    const timedOut = session.getItem(SessionStorageKey.sessionTimedOut)
    if (timedOut && !sessionTimedOut) {
      setSessionTimedOut(!sessionTimedOut)
    }
  }, [sessionTimedOut])

  const handleLogin = async (values: Store) => {
    const { email, password } = values as FormValues
    try {
      state.start()
      setEmail(email)
      await login({ username: email, password })
      setOtpForm(!otpForm)
    } catch (e) {
      if (e.response.status === 429) {
        state.setError(t(copy.Inputs.Errors.requestThrottle))
      } else if (e.response.status === 401) {
        state.setError(t(copy.Inputs.Errors.invalidCredentials))
      } else {
        state.setError(e.response?.data?.detail)
      }
      state.end()
    }
  }

  return (
    <main className={styles.login}>
      <section className={styles.left}>
        <div className={styles.content}>
          {otpForm ? (
            <OTPForm email={email} />
          ) : (
            <Form onFinish={handleLogin}>
              {sessionTimedOut && (
                <Alert
                  message={t(copy.Pages.Login.alert)}
                  type="warning"
                  className={styles.alert}
                  icon={<Icon name="alert" type="warning" />}
                  showIcon
                />
              )}
              <Heading size="2xl" className={styles.heading}>
                {t(copy.Pages.Login.heading)}
              </Heading>
              <Text paragraph className={styles.subheading}>
                {t(copy.Pages.Login.subheading)}
              </Text>
              <ErrorAlert message={state.error} />
              <Input
                required
                name="email"
                type="email"
                label={t(copy.Inputs.Labels.email)}
                placeholder={t(copy.Inputs.Labels.email)}
              />
              <Input
                password
                required
                name="password"
                label={t(copy.Inputs.Labels.password)}
                placeholder={t(copy.Inputs.Labels.password)}
              />
              <Button
                block
                htmlType="submit"
                loading={state.loading}
                className={styles.button}
              >
                {t(copy.Touchables.Buttons.login)}
              </Button>
              <Button type="link" className={styles.link}>
                <Link to="/forgot_password">
                  {t(copy.Touchables.Links.forgotPassword)}
                </Link>
              </Button>
            </Form>
          )}
        </div>
      </section>

      <Testimonials className={styles.right} variant={1} />
    </main>
  )
}

export default Login
